import { TellescopeModelFromName } from "@tellescope/tanstack-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export type VitalComparisonType = "Less Than" | "Greater Than" | "Between";

export type RangeRow = {
  id: string;
  classification: string;
  comparisonType: VitalComparisonType;
  value: string;
  valueLower: string;
  valueUpper: string;
  trendIntervalInMS: string;
};

export const newRangeRow = (): RangeRow => ({
  id: crypto.randomUUID(),
  classification: "Target",
  comparisonType: "Greater Than",
  value: "",
  valueLower: "",
  valueUpper: "",
  trendIntervalInMS: "",
});

export const rangeRowToRange = (row: RangeRow): TellescopeModelFromName<"vital_configurations">["ranges"][number] => {
  if (row.comparisonType === "Between") {
    return {
      classification: row.classification,
      comparison: {
        type: "Between",
        value: { lower: parseFloat(row.valueLower) || 0, upper: parseFloat(row.valueUpper) || 0 },
      },
      trendIntervalInMS: row.trendIntervalInMS ? parseFloat(row.trendIntervalInMS) : undefined,
    };
  }
  return {
    classification: row.classification,
    comparison: {
      type: row.comparisonType,
      value: parseFloat(row.value) || 0,
    },
    trendIntervalInMS: row.trendIntervalInMS ? parseFloat(row.trendIntervalInMS) : undefined,
  };
};

export const rangeToRow = (range: TellescopeModelFromName<"vital_configurations">["ranges"][number]): RangeRow => {
  const row: RangeRow = {
    id: crypto.randomUUID(),
    classification: range.classification,
    comparisonType: range.comparison.type,
    value: "",
    valueLower: "",
    valueUpper: "",
    trendIntervalInMS: range.trendIntervalInMS?.toString() ?? "",
  };
  if (range.comparison.type === "Between") {
    row.valueLower = range.comparison.value.lower.toString();
    row.valueUpper = range.comparison.value.upper.toString();
  } else {
    row.value = range.comparison.value.toString();
  }
  return row;
};

export const useVitalConfigurationForm = (
  config: TellescopeModelFromName<"vital_configurations"> | null,
  onSave: (data: {
    title: string;
    unit: string;
    ranges: TellescopeModelFromName<"vital_configurations">["ranges"];
  }) => Promise<void>,
  onCancel: () => void
) => {
  const { t } = useTranslation("vitals");
  const [title, setTitle] = useState(config?.title ?? "");
  const [unit, setUnit] = useState(config?.unit ?? "");
  const [ranges, setRanges] = useState<RangeRow[]>(() => config?.ranges.map(rangeToRow) ?? []);
  const [rangesError, setRangesError] = useState("");

  const addRange = () => {
    setRanges((prev) => [...prev, newRangeRow()]);
  };

  const removeRange = (id: string) => {
    setRanges((prev) => prev.filter((r) => r.id !== id));
  };

  const updateRange = (id: string, updates: Partial<RangeRow>) => {
    setRanges((prev) => prev.map((r) => (r.id === id ? { ...r, ...updates } : r)));
  };

  const reorderRanges = (newRanges: RangeRow[]) => {
    setRanges(newRanges);
  };

  const handleSave = async () => {
    if (ranges.length === 0) {
      setRangesError(t("Must have a valid range for vitals"));
      return;
    }
    setRangesError("");
    await onSave({
      title,
      unit,
      ranges: ranges.map(rangeRowToRange),
    });
  };

  return {
    title,
    setTitle,
    unit,
    setUnit,
    ranges,
    rangesError,
    addRange,
    removeRange,
    updateRange,
    reorderRanges,
    handleSave,
    onCancel,
  };
};
